/** One event of a text/event-stream, as the HTML specification defines it. */
export type SseMessage = { event: string; data: string; id?: string };

export type SseParserHandlers = {
  onMessage: (message: SseMessage) => void;
  /** The `retry:` field: how long the server wants the client to wait before reconnecting. */
  onRetry?: (ms: number) => void;
  /** Any line arrived, comments included: used as a heartbeat. */
  onActivity?: () => void;
};

/**
 * Incremental parser for server-sent events. Chunks may cut lines and even a CRLF pair anywhere;
 * comments (`: keepalive`) are ignored; an event is dispatched on the blank line after it.
 */
export class SseParser {
  private buffer = '';
  private dataLines: string[] = [];
  private eventName = '';
  private lastId: string | undefined;

  constructor(private readonly handlers: SseParserHandlers) {}

  push(chunk: string): void {
    if (!chunk) return;
    this.buffer += chunk;
    let start = 0;
    for (let index = 0; index < this.buffer.length; index++) {
      const char = this.buffer[index];
      if (char !== '\n' && char !== '\r') continue;
      // A CR at the very end may be the first half of a CRLF still on its way.
      if (char === '\r' && index === this.buffer.length - 1) break;
      this.line(this.buffer.slice(start, index));
      if (char === '\r' && this.buffer[index + 1] === '\n') index++;
      start = index + 1;
    }
    this.buffer = this.buffer.slice(start);
  }

  /** Clears everything, for a new connection. A half-received event is discarded, as the spec says. */
  reset(): void {
    this.buffer = '';
    this.dataLines = [];
    this.eventName = '';
  }

  private line(line: string): void {
    this.handlers.onActivity?.();
    if (line === '') {
      this.dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        this.eventName = value;
        break;
      case 'data':
        this.dataLines.push(value);
        break;
      case 'id':
        if (!value.includes('\0')) this.lastId = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) this.handlers.onRetry?.(Number(value));
        break;
      default:
        break;
    }
  }

  private dispatch(): void {
    if (this.dataLines.length === 0) {
      this.eventName = '';
      return;
    }
    const message: SseMessage = { event: this.eventName || 'message', data: this.dataLines.join('\n') };
    if (this.lastId !== undefined) message.id = this.lastId;
    this.dataLines = [];
    this.eventName = '';
    this.handlers.onMessage(message);
  }
}
